import { useCallback, useEffect, useRef, useState } from 'react';
import mqtt from 'mqtt';
import { AclSession, AclStatus, MqttAclAdapter } from '../services/mqtt/aclSession';
import type { MqttHookReturn, MqttPayload, SubscriptionCallback } from './useMqtt';

export function useAclMqtt(enabled: boolean, adapter?: MqttAclAdapter): MqttHookReturn {
  const [connectionStatus, setStatus] = useState<AclStatus>('disconnected');
  const [messages, setMessages] = useState<Record<string, MqttPayload>>({});
  const session = useRef<AclSession | null>(null);
  const callbacks = useRef(new Set<SubscriptionCallback>());
  const demandCallbacks = useRef(new Set<SubscriptionCallback>());
  const receive = useCallback((raw: string) => {
    let parsed: MqttPayload;
    try { parsed = JSON.parse(raw); } catch { parsed = raw; }
    setMessages({ 'eternal/demand': parsed });
    callbacks.current.forEach(callback => callback(parsed));
    demandCallbacks.current.forEach(callback => callback(parsed));
  }, []);
  const principal = adapter?.identity.principalId;
  const tenant = adapter?.identity.tenantId;
  const revision = adapter?.identity.revision;
  const scope = useRef<string>();
  const broker = adapter?.brokerUrl;
  const mint = adapter?.mint;

  useEffect(() => {
    setMessages({});
    const nextScope = JSON.stringify([principal, tenant]);
    if (scope.current !== nextScope) {
      callbacks.current.clear();
      demandCallbacks.current.clear();
      scope.current = nextScope;
    }
    if (!enabled || !adapter) {
      setStatus(enabled ? 'error' : 'disconnected');
      return;
    }
    const current = new AclSession(adapter, mqtt.connect, setStatus);
    session.current = current;
    current.subscribe('eternal/demand', receive);
    current.start();
    return () => {
      current.stop();
      session.current = null;
    };
    // Identity values, endpoint and mint function define one credential scope.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, principal, tenant, revision, broker, mint, receive]);

  const subscribe = useCallback((topic: string, callback?: SubscriptionCallback) => {
    if (!session.current || topic !== 'eternal/demand') return false;
    if (callback) callbacks.current.add(callback);
    return session.current.subscribe(topic, receive);
  }, [receive]);
  const unsubscribe = useCallback((topic: string) => {
    if (topic !== 'eternal/demand') return false;
    callbacks.current.clear();
    return session.current?.unsubscribe(topic) ?? false;
  }, []);
  const publish = useCallback((topic: string, message: string | Record<string, any>) => {
    try {
      return session.current?.publish(topic,
        typeof message === 'object' ? JSON.stringify(message) : message) ?? false;
    } catch { return false; }
  }, []);
  const registerDemandCallback = useCallback((callback: SubscriptionCallback) => {
    demandCallbacks.current.add(callback);
  }, []);
  const unregisterDemandCallback = useCallback((callback: SubscriptionCallback) => {
    demandCallbacks.current.delete(callback);
  }, []);
  const clearDemandCallbacks = useCallback(() => { demandCallbacks.current.clear(); }, []);

  return {
    connectionStatus, messages, client: null, subscribe, unsubscribe, publish,
    registerDemandCallback, unregisterDemandCallback, clearDemandCallbacks
  };
}
